import { useId, useState, useEffect, useMemo } from 'react';
import {
  Container,
  Button,
  Alert,
  Spinner,
  Card,
  Row,
  Col,
  Table,
  Badge,
  Form,
} from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sampleService, Sample } from '../services/sample.service';
import { format } from 'date-fns';
import { formatSampleDateTime, formatSampleDateTimeLong } from '../utils/dateFnsLocale';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DEFAULT_USAGE_RECORD_QUANTITY,
  USAGE_ACTION,
  USAGE_ACTION_VALUES,
} from '../constants/sample.constants';
import { formatSampleCreatorLabel, formatUserName } from '../utils/name.util';
import {
  canSampleAcceptUsageRecord,
  sampleRemainingQuantityBadgeBg,
  sortAndEnrichUsageRecords,
} from '../utils/sampleUsageRecords';
import {
  formatUsageRecordActionLabel,
  usageActionBadgeBg,
} from '../utils/sampleUsageActionDisplay';
import { formatSampleTypeDisplay } from '../utils/sampleTypeLabels';
import { formatSampleQuantityWithUnit } from '../utils/sampleUnitLabels';
import { joinLabeledValue } from '../utils/joinLabeledValue';
import { getAxiosOrErrorMessage } from '../utils/error.utils';
import { logger, toLoggableError } from '../utils/logger';
import TextContentSep from '../components/common/TextContentSep';
import BaseDateTimeField from '../components/samples/BaseDateTimeField';
import PublicShareHero from '../components/samples/PublicShareHero';
import { SampleFactRow } from '../components/samples/SampleDetailFactsPanels';
import SampleDetailPathogenRows from '../components/samples/SampleDetailPathogenRows';
import { SITE_NAME } from '../constants/siteSeo';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../utils/softBreakIdentifier';
import { buildPublicSampleEncryptedPath } from '../utils/publicShareUrl';

const usageRecordSchema = (t: (key: string) => string) =>
  z.object({
    quantity: z.number().int().min(0, t('usage_record.quantity_min_error')),
    usageDate: z.string().min(1, t('usage_record.usage_date_required')),
    purpose: z.string().optional(),
    notes: z.string().optional(),
    action: z.enum([...USAGE_ACTION_VALUES] as [string, ...string[]]).optional(),
    recordedBy: z.string().min(1, t('usage_record.recorded_by_required')),
  });

type UsageRecordFormData = z.infer<ReturnType<typeof usageRecordSchema>>;

function resolveApiMessage(
  raw: string | undefined,
  t: (key: string, opts?: { defaultValue?: string }) => string
): string {
  if (!raw) return '';
  const isKey =
    raw.startsWith('sample.') ||
    raw.startsWith('usage_record.') ||
    raw.startsWith('common.') ||
    raw.startsWith('api.error.');
  return isKey ? t(raw, { defaultValue: raw }) : raw;
}

type PublicSampleDetailLocationState = {
  fromLocationShare?: string;
};

export default function PublicSampleDetail() {
  const { t, i18n } = useTranslation();
  const usageRecordFieldId = useId();
  const { encryptedId } = useParams<{ encryptedId: string }>();
  const location = useLocation();
  const fromLocationShare = (location.state as PublicSampleDetailLocationState | null)
    ?.fromLocationShare;
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const [recordedByName, setRecordedByName] = useState(() => {
    return localStorage.getItem('public_access_recorder_name') || '';
  });

  const { data, isLoading, isError, error: queryError } = useQuery({
    queryKey: ['samples', 'public', encryptedId, i18n.language],
    queryFn: () => sampleService.getByEncryptedId(encryptedId!),
    enabled: !!encryptedId,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UsageRecordFormData>({
    resolver: zodResolver(usageRecordSchema(t)),
    defaultValues: {
      quantity: DEFAULT_USAGE_RECORD_QUANTITY,
      usageDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      purpose: '',
      notes: '',
      action: USAGE_ACTION.USE,
      recordedBy: recordedByName || '',
    },
  });
  const usageDate = watch('usageDate');

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'recordedBy' && typeof value.recordedBy === 'string') {
        const trimmed = value.recordedBy.trim();
        if (trimmed) {
          localStorage.setItem('public_access_recorder_name', trimmed);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (encryptedId) {
      setPublicUrl(
        `${window.location.origin}${buildPublicSampleEncryptedPath(encryptedId)}`
      );
    }
  }, [encryptedId]);

  const addUsageMutation = useMutation({
    mutationFn: (form: UsageRecordFormData) =>
      sampleService.addUsageRecordByEncryptedId(encryptedId!, {
        quantity: form.quantity,
        usageDate: form.usageDate,
        purpose: form.purpose,
        notes: form.notes,
        action: form.action || USAGE_ACTION.USE,
        recordedBy: form.recordedBy,
      }),
    onSuccess: (_data, form) => {
      queryClient.invalidateQueries({ queryKey: ['samples', 'public', encryptedId] });
      setSuccessMessage(t('usage_record.success', { defaultValue: 'Usage record added.' }));
      setError('');
      const nextRecorder = form.recordedBy?.trim() || recordedByName || '';
      if (nextRecorder) {
        localStorage.setItem('public_access_recorder_name', nextRecorder);
        setRecordedByName(nextRecorder);
      }
      reset({
        quantity: DEFAULT_USAGE_RECORD_QUANTITY,
        usageDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        purpose: '',
        notes: '',
        action: USAGE_ACTION.USE,
        recordedBy: nextRecorder,
      });
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: (err: unknown) => {
      setError(resolveApiMessage(getAxiosOrErrorMessage(err), t));
      setSuccessMessage('');
    },
  });

  const sample: Sample | undefined = data?.data;
  const canAddUsage = !!sample && canSampleAcceptUsageRecord(sample);
  const creatorLabel = sample ? formatSampleCreatorLabel(sample, i18n.language) : '-';

  const usageRows = useMemo(() => {
    if (!sample?.usageRecords?.length) return [];
    return sortAndEnrichUsageRecords(sample.usageRecords, sample.initialQuantity);
  }, [sample]);

  useEffect(() => {
    if (!sample?.name) return;
    const prev = document.title;
    const displayName = formatEntityDisplayName(sample.name, t);
    document.title = `${displayName} · ${t('sample.public_access_title_short', {
      defaultValue: '公開サンプル詳細',
    })} · ${SITE_NAME}`;
    return () => {
      document.title = prev;
    };
  }, [sample?.name, t]);

  // サンプル切替時のみフォームを初期化（recordedBy 入力中の reset ループを避ける）
  useEffect(() => {
    if (!sample) return;
    const persisted =
      recordedByName ||
      (typeof window !== 'undefined'
        ? localStorage.getItem('public_access_recorder_name') || ''
        : '');
    reset({
      quantity: DEFAULT_USAGE_RECORD_QUANTITY,
      usageDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      purpose: '',
      notes: '',
      action: USAGE_ACTION.USE,
      recordedBy: persisted,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sample.id のみ。recordedByName 連動で入力を消さない
  }, [sample?.id, reset]);

  const onSubmitForm = async (form: UsageRecordFormData) => {
    try {
      setError('');
      setSuccessMessage('');

      if (sample?.disposalDate) {
        setError(t('sample.already_disposed'));
        return;
      }

      if (
        sample &&
        sample.remainingQuantity !== undefined &&
        form.quantity > sample.remainingQuantity
      ) {
        setError(
          t('usage_record.quantity_exceeds_remaining', {
            remaining: formatSampleQuantityWithUnit(
              sample.remainingQuantity,
              sample.unit,
              t
            ),
          })
        );
        return;
      }

      await addUsageMutation.mutateAsync(form);
    } catch (err: unknown) {
      logger.error('Usage record add error', toLoggableError(err));
      const msg = resolveApiMessage(getAxiosOrErrorMessage(err), t);
      if (msg) setError(msg);
    }
  };

  const copyUrlToClipboard = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setSuccessMessage(t('sample.url_copied', { defaultValue: 'URL copied to clipboard.' }));
      window.setTimeout(() => {
        setCopied(false);
        setSuccessMessage('');
      }, 2500);
    } catch {
      setCopied(false);
    }
  };

  if (isLoading) {
    return (
      <Container className="mt-4" id="public-sample-detail-page">
        <div className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (isError || !sample) {
    const is404Error =
      (queryError as { response?: { status?: number } } | null)?.response?.status === 404;
    const errorMessage = is404Error
      ? t('sample.not_found_or_deleted', {
          defaultValue: 'This sample was deleted or does not exist.',
        })
      : queryError instanceof Error
        ? resolveApiMessage(queryError.message, t) || queryError.message
        : t('sample.load_failed', { defaultValue: 'Failed to load sample data.' });

    return (
      <Container className="mt-4" id="public-sample-detail-page">
        <Alert variant="danger">
          <Alert.Heading>
            <i className="fas fa-exclamation-triangle me-2" aria-hidden="true" />
            {is404Error
              ? t('sample.sample_not_found', { defaultValue: 'Sample not found' })
              : t('sample.error_occurred', { defaultValue: 'An error occurred' })}
          </Alert.Heading>
          <p className="mb-0">{errorMessage}</p>
        </Alert>
      </Container>
    );
  }

  const remaining =
    sample.remainingQuantity !== null && sample.remainingQuantity !== undefined
      ? sample.remainingQuantity
      : null;
  const storageLabel = sample.storageRoom?.name
    ? softBreakIdentifier(sample.storageRoom.name, t)
    : '-';

  return (
    <div className="bg-light min-vh-100" id="public-sample-detail-page">
      <PublicShareHero
        iconClassName="fa-flask"
        title={t('sample.public_access_title_short', { defaultValue: '公開サンプル詳細' })}
        description={t('sample.public_access_description', {
          defaultValue: 'View and record sample information',
        })}
        copied={copied}
        onCopy={() => void copyUrlToClipboard()}
      />

      <Container className="pb-4 px-3">
        {fromLocationShare ? (
          <div className="mb-2">
            <Link
              to={fromLocationShare}
              className="btn btn-link btn-sm text-decoration-none px-0 public-sample-back-link"
            >
              <i className="fas fa-arrow-left me-1" aria-hidden="true" />
              {t('sample.back_to_list', { defaultValue: '一覧に戻る' })}
            </Link>
          </div>
        ) : null}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        <Card className="shadow-sm border-0 mb-3">
          <Card.Header className="bg-primary text-white py-2 px-3">
            <div className="d-flex justify-content-between align-items-center gap-2">
              <h2 className="h6 mb-0 text-truncate">
                {softBreakIdentifier(sample.name, t)}
              </h2>
              <div className="d-flex flex-shrink-0 gap-1">
                <Badge bg="light" text="dark">
                  {joinLabeledValue(t('common.id'), String(sample.id))}
                </Badge>
                {sample.disposalDate && (
                  <Badge bg="secondary">{t('sample_filter.status_disposed')}</Badge>
                )}
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-3">
            <div className="public-sample-summary mb-3" aria-label={t('sample.storage_info')}>
              <Badge
                bg={sampleRemainingQuantityBadgeBg(sample)}
                className="public-sample-qty"
                title={t('sample.remaining_quantity')}
              >
                {joinLabeledValue(
                  t('sample.remaining_quantity_short', { defaultValue: '残' }),
                  formatSampleQuantityWithUnit(remaining, sample.unit, t)
                )}
              </Badge>
              <TextContentSep />
              <span className="text-muted small text-truncate">{storageLabel}</span>
              <TextContentSep />
              <span className="text-muted small text-truncate">
                {formatSampleTypeDisplay(sample.sampleType, t)}
                {sample.bslLevel ? ` · ${sample.bslLevel}` : ''}
              </span>
            </div>

            <Row className="g-2 mb-3">
              <Col md={6}>
                <Card className="border h-100">
                  <Card.Header className="py-1 px-3 small fw-semibold bg-white">
                    <i className="fas fa-info-circle me-2 text-muted" aria-hidden="true" />
                    {t('sample.basic_info')}
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table size="sm" className="mb-0 public-sample-meta-table">
                      <tbody>
                        <SampleFactRow label={t('sample.sample_name')}>
                          {softBreakIdentifier(sample.name, t)}
                        </SampleFactRow>
                        <SampleFactRow label={t('sample.sample_type')}>
                          {formatSampleTypeDisplay(sample.sampleType, t)}
                        </SampleFactRow>
                        <SampleFactRow
                          label={t('sample.bsl_level_short', { defaultValue: 'BSL' })}
                          labelTitle={t('sample.bsl_level')}
                        >
                          {sample.bslLevel || '-'}
                        </SampleFactRow>
                        <SampleFactRow
                          label={t('sample.creator', { defaultValue: '作製者' })}
                          labelTitle={t('sample.maker_name')}
                        >
                          {creatorLabel}
                        </SampleFactRow>
                        <SampleFactRow label={t('sample.created_at')}>
                          {sample.createdAt
                            ? formatSampleDateTimeLong(sample.createdAt, i18n.language)
                            : '-'}
                        </SampleFactRow>
                        <SampleDetailPathogenRows sample={sample} />
                        {sample.notes ? (
                          <SampleFactRow label={t('sample.notes')}>
                            <span className="text-break" style={{ whiteSpace: 'pre-wrap' }}>
                              {sample.notes}
                            </span>
                          </SampleFactRow>
                        ) : null}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="border h-100">
                  <Card.Header className="py-1 px-3 small fw-semibold bg-white">
                    <i className="fas fa-warehouse me-2 text-muted" aria-hidden="true" />
                    {t('sample.storage_info')}
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table size="sm" className="mb-0 public-sample-meta-table">
                      <tbody>
                        <SampleFactRow label={t('sample.storage_room')}>
                          {storageLabel}
                          {sample.storageRoom?.location ? (
                            <span
                              className="text-muted ms-1 small"
                              title={t('storage_room.location', { defaultValue: '設置場所' })}
                            >
                              · {sample.storageRoom.location}
                            </span>
                          ) : null}
                        </SampleFactRow>
                        {sample.drawerNumber != null ? (
                          <SampleFactRow
                            label={t('sample.drawer_short', { defaultValue: '引き出し' })}
                            labelTitle={t('sample.drawer_number')}
                          >
                            {sample.drawerNumber}
                          </SampleFactRow>
                        ) : null}
                        {sample.caseName ? (
                          <SampleFactRow label={t('sample.case')}>
                            {sample.caseName}
                          </SampleFactRow>
                        ) : null}
                        {sample.storageDate ? (
                          <SampleFactRow label={t('sample.storage_date')}>
                            {formatSampleDateTimeLong(sample.storageDate, i18n.language)}
                          </SampleFactRow>
                        ) : null}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <section className="public-sample-usage-form mb-3" aria-labelledby={`${usageRecordFieldId}-form-title`}>
              <h3 className="h6 mb-2" id={`${usageRecordFieldId}-form-title`}>
                <i className="fas fa-plus-circle me-2 text-primary" aria-hidden="true" />
                {t('sample.add_usage_record', { defaultValue: 'Add usage record' })}
              </h3>
              {canAddUsage ? (
                <Form onSubmit={handleSubmit(onSubmitForm)} className="public-sample-form">
                  <Row className="g-2">
                    <Col xs={12} sm={6} md={3}>
                      <Form.Group>
                        <BaseDateTimeField
                          id={`${usageRecordFieldId}-usage-date`}
                          label={t('usage_record.usage_date')}
                          required
                          size="sm"
                          value={usageDate}
                          onChange={(v) =>
                            setValue('usageDate', v, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                          }
                          isInvalid={!!errors.usageDate}
                          lang={i18n.language}
                        />
                        {errors.usageDate ? (
                          <div className="invalid-feedback d-block">
                            {errors.usageDate.message}
                          </div>
                        ) : null}
                      </Form.Group>
                    </Col>
                    <Col xs={6} sm={3} md={2}>
                      <Form.Group>
                        <Form.Label htmlFor={`${usageRecordFieldId}-action`} className="small mb-1">
                          {t('usage_record.action')} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          id={`${usageRecordFieldId}-action`}
                          size="sm"
                          {...register('action')}
                          isInvalid={!!errors.action}
                        >
                          <option value={USAGE_ACTION.USE}>{t('usage_record.action_use')}</option>
                          <option value={USAGE_ACTION.DISPOSE}>
                            {t('usage_record.action_dispose')}
                          </option>
                          <option value={USAGE_ACTION.MOVE}>{t('usage_record.action_move')}</option>
                          <option value={USAGE_ACTION.OTHER}>
                            {t('usage_record.action_other')}
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={6} sm={3} md={2}>
                      <Form.Group>
                        <Form.Label htmlFor={`${usageRecordFieldId}-quantity`} className="small mb-1">
                          {t('usage_record.quantity')}
                        </Form.Label>
                        <Form.Control
                          id={`${usageRecordFieldId}-quantity`}
                          size="sm"
                          type="number"
                          min="0"
                          step="1"
                          {...register('quantity', { valueAsNumber: true })}
                          isInvalid={!!errors.quantity}
                        />
                        {errors.quantity && (
                          <Form.Control.Feedback type="invalid">
                            {errors.quantity.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={5}>
                      <Form.Group>
                        <Form.Label htmlFor={`${usageRecordFieldId}-purpose`} className="small mb-1">
                          {t('usage_record.detail')}
                        </Form.Label>
                        <Form.Control
                          id={`${usageRecordFieldId}-purpose`}
                          size="sm"
                          type="text"
                          placeholder={t('usage_record.detail_placeholder')}
                          {...register('purpose')}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={8} md={6}>
                      <Form.Group>
                        <Form.Label
                          htmlFor={`${usageRecordFieldId}-recorded-by`}
                          className="small mb-1"
                        >
                          {t('usage_record.recorded_by')} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          id={`${usageRecordFieldId}-recorded-by`}
                          size="sm"
                          type="text"
                          {...register('recordedBy')}
                          isInvalid={!!errors.recordedBy}
                        />
                        {errors.recordedBy && (
                          <Form.Control.Feedback type="invalid">
                            {errors.recordedBy.message}
                          </Form.Control.Feedback>
                        )}
                        <Form.Text className="text-muted d-none d-md-block">
                          {t('usage_record.recorded_by_hint')}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={4} md={6} className="d-flex align-items-end">
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        className="public-sample-submit-btn"
                        disabled={addUsageMutation.isPending || !canAddUsage}
                      >
                        {addUsageMutation.isPending ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t('common.saving')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-1" aria-hidden="true" />
                            {t('usage_record.add')}
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              ) : (
                <Alert variant="warning" className="mb-0 py-2">
                  {sample.disposalDate
                    ? t('sample.already_disposed')
                    : t('sample.cannot_add_usage')}
                </Alert>
              )}
            </section>

            <Card className="border mb-3">
              <Card.Header className="py-1 px-3 small fw-semibold bg-white">
                <i className="fas fa-history me-2 text-muted" aria-hidden="true" />
                {t('sample.usage_records')}
              </Card.Header>
              <Card.Body className="p-0">
                {usageRows.length > 0 ? (
                  <div className="table-responsive">
                    <Table striped hover size="sm" className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t('usage_record.table_header_usage_date')}</th>
                          <th>{t('usage_record.table_header_action')}</th>
                          <th className="d-none d-md-table-cell">
                            {t('usage_record.table_header_detail')}
                          </th>
                          <th className="text-end">
                            {t('usage_record.table_header_quantity', { defaultValue: '数量' })}
                          </th>
                          <th className="text-end">
                            {t('sample.remaining_quantity', { defaultValue: '残数量' })}
                          </th>
                          <th>{t('usage_record.table_header_recorded_by')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageRows.map((record) => {
                          const actionText = formatUsageRecordActionLabel(record, t);
                          const recorder =
                            (record.user
                              ? formatUserName(
                                  record.user.firstName,
                                  record.user.lastName,
                                  i18n.language
                                )
                              : '') ||
                            record.user?.username ||
                            record.recordedBy ||
                            '-';
                          return (
                            <tr
                              key={record.id}
                              className={record.isCreationRecord ? 'table-success' : undefined}
                            >
                              <td className="text-nowrap">
                                {record.usageDate
                                  ? formatSampleDateTime(record.usageDate, i18n.language)
                                  : '-'}
                              </td>
                              <td>
                                {record.isCreationRecord ? (
                                  <Badge bg="success">{actionText}</Badge>
                                ) : (
                                  <Badge bg={usageActionBadgeBg(record.action)}>
                                    {actionText}
                                  </Badge>
                                )}
                              </td>
                              <td className="d-none d-md-table-cell">{record.notes || '-'}</td>
                              <td className="text-end">
                                {record.isCreationRecord
                                  ? '-'
                                  : formatSampleQuantityWithUnit(
                                      record.quantity,
                                      sample.unit,
                                      t
                                    )}
                              </td>
                              <td className="text-end fw-semibold text-primary">
                                {formatSampleQuantityWithUnit(
                                  record.calculatedRemaining,
                                  sample.unit,
                                  t
                                )}
                              </td>
                              <td className="text-truncate" style={{ maxWidth: '7rem' }}>
                                {recorder}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <p className="mb-0 small text-muted text-center py-3" role="status">
                    {t('usage_record.no_records')}
                  </p>
                )}
              </Card.Body>
            </Card>

            <details className="public-sample-help text-muted small">
              <summary className="mb-1">
                <i className="fas fa-info-circle me-1" aria-hidden="true" />
                {t('sample.public_access_info')}
              </summary>
              <ul className="mb-2 ps-3">
                <li>
                  <strong>{t('sample.viewable')}</strong>
                  {' · '}
                  {t('sample.viewable_description')}
                </li>
                <li>
                  <strong>{t('sample.recordable')}</strong>
                  {' · '}
                  {t('sample.recordable_description')}
                </li>
                <li>
                  <strong>{t('sample.restrictions')}</strong>
                  {' · '}
                  {t('sample.restrictions_description')}
                </li>
                <li>
                  <strong>{t('sample.recorder')}</strong>
                  {' · '}
                  {t('sample.recorder_description')}
                </li>
              </ul>
              <p className="mb-0">
                <i className="fas fa-shield-alt me-1" aria-hidden="true" />
                {t('sample.security')}
                {' · '}
                {t('sample.security_description')}
              </p>
            </details>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
